<template>
  <div :class="_class">
    <input
      ref="inputFile"
      type="file"
      class="ten-upload__input"
      :name="inputName"
      :multiple="multiple"
      :accept="accept"
      @change="onInputFileChange"
    >
    <div
      :class="_uploadContanerClass"
      v-on="this._uploadContanerEvents"
    >
      <slot :dragOver="dragOver">
        <ten-button
          class="ten-upload__trigger"
          icon="upload"
          v-if="listType==='text'"
        >
          上传文件
        </ten-button>
        <div class="ten-upload__trigger--picture-card">
          <ten-icon icon="add" />
        </div>
      </slot>
    </div>
    <!-- @slot 自定义 fileList，作用域插槽，接收 fileList 作为参数 -->
    <slot
      name="fileList"
      :fileList="fileList"
    >
      <ten-file-list
        class="ten-upload__file-list"
        :list="fileList"
        @remove="onRemoveFile"
        @preview="onPreviewFile"
        v-if="showFileList"
      />
    </slot>
  </div>
</template>

<script>
import defaultRequest from './request';
import TenButton from '../button';
import TenIcon from '../icon';
import TenFileList from './file-list';

const name = 'ten-upload';
const FILE_STATUS = {
  READY: 'ready',
  DONE: 'done',
  LOADING: 'loading',
  ERROR: 'error',
};

export default {
  name,
  components: {
    TenButton,
    TenIcon,
    TenFileList,
  },
  props: {
    /**
     * @model 文件列表
     */
    fileList: {
      type: Array,
      default() {
        return [];
      },
    },
    /**
     * 是否禁用
     */
    disabled: Boolean,
    /**
     * 上传列表样式
     * @member text | picture-card
     */
    listType: {
      type: String,
      default: 'text',
    },
    /**
     * 服务器接收到的文件名
     */
    inputName: {
      type: String,
      default: 'file',
    },
    /**
     * 接收文件的服务器地址
     */
    url: {
      type: String,
    },
    /**
     * 限制上传的文件类型
     */
    accept: {
      type: String,
    },
    /**
     * 限制上传文件数，建议使用受控的形式传入 fileList 灵活控制
     */
    limit: {
      type: Number,
    },
    /**
     * 使用拖拽模式
     */
    drag: {
      type: Boolean,
      default: false,
    },
    /**
     * 拖拽模式下，只使用拖拽触发上传，点击不触发
     */
    onlyDrag: Boolean,
    /**
     * 是否多选
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * 设置请求头信息
     */
    headers: {
      type: Object,
      default() {
        return {};
      },
    },
    /**
     * 带 cookie 凭证信息
     */
    withCredentials: Boolean,
    /**
     * 设置请求的其他参数
     */
    formData: {
      type: Object,
      default() {
        return {};
      },
    },
    /**
     * 自定义上传方法
     */
    request: Function,
    /**
     * 是否显示 fileList
     */
    showFileList: {
      type: Boolean,
      default: true,
    },
    /**
     * 上传文件之前的钩子，参数为上传的文件，若返回 false 则停止上传。
     * 支持返回一个 Promise 对象，Promise 对象 reject 时则停止上传，
     * resolve 时开始上传（ resolve 传入 File 或 Blob 对象则上传 resolve 传入对象）
     */
    beforeUpload: Function,
    /**
     * 删除回调，如果回调返回，如果提供此参数且回调返回假值则不删除
     * 如不提供此参数或回调返回真值则删除
     */
    onRemove: Function,
    /**
     * 文件列表点击回调可用于实现预览或者下载
     */
    onPreview: Function,

    // will be discard
    /**
     * @ignore
     */
    onStart: Function,
    /**
     * @ignore
     */
    onProgress: Function,
    /**
     * @ignore
     */
    onSuccess: Function,
    /**
     * @ignore
     */
    onError: Function,
    /**
     * @ignore
     */
    onChange: Function,
    /**
     * 是否自动上传
     */
    autoUpload: {
      type: Boolean,
      default: true,
    },
    /**
     * 上传请求的 http method
     */
    method: {
      type: String,
      default: 'POST',
    },
  },
  data() {
    return {
      dragOver: false,
    };
  },
  computed: {
    _class() {
      return [
        'ten-upload',
        {
          'ten-upload--disabled': this.disabled,
          'ten-upload--drag': this.drag,
          'ten-upload--picture-card': this.listType === 'picture-card',
        },
      ];
    },
    _uploadContanerClass() {
      return [
        'ten-upload__select-container',
        {
          'ten-upload__select-container--dragover': this.dragOver,
        },
      ];
    },
    _uploadContanerEvents() {
      const events = {};
      if (this.disabled) {
        return events;
      }
      if (this.drag) {
        events.dragover = this.onDragOver;
        events.dragleave = this.onDragLeave;
        events.drop = this.onDrop;
        if (!this.onlyDrag) {
          events.click = this.onSelectFile;
        }
      } else {
        events.click = this.onSelectFile;
      }
      return events;
    },
  },
  watch: {
    fileList: {
      handler(fileList) {
        this.updateUploadList();
        fileList.forEach((fileObj) => {
          if (
            this.autoUpload
            && fileObj.key
            && fileObj.status === FILE_STATUS.READY
            && !this.uploadList.find(uploadItem => uploadItem.key === fileObj.key)
          ) {
            this.uploadFile(fileObj);
          }
        });
      },
    },
  },
  created() {
    this.uploadList = [];
  },
  methods: {
    addFiles(files) {
      const { accept, beforeUpload } = this;
      let newFiles = this.checkAccepts(accept, files);
      if (this.limit) {
        const surplus = Math.max(this.limit - this.uploadList.length, 0);
        if (surplus < newFiles.length) {
          const exceedFiles = newFiles.slice(surplus);
          newFiles = newFiles.slice(0, surplus);
          /**
           * 设置 limit 之后，文件超出限制事件，文件超出拒绝添加到 fileList
           * 如需实现先进先出等个性效果，建议通过自行控制 fileList 实现（不设置 limit）
           * @param {Array} exceedFiles
           */
          this.$emit('exceed', exceedFiles);
        }
      }
      if (newFiles.length === 0) return;
      this.uploadFiles(newFiles.map(file => ({
        file,
        result: beforeUpload ? beforeUpload(file) : undefined,
      })));
    },
    checkAccepts(accept, files) {
      if (!accept) return Array.from(files);
      const newFiles = [];
      const rejectedFiles = [];
      const acceptList = accept.split(',');
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        let accepted = false;
        for (let i = 0; i < acceptList.length; i++) {
          const type = acceptList[i];
          const reg = new RegExp(
            `${type
              .trim()
              .replace('.', '\\.')
              .replace('*', '.+')}$`,
            'i'
          );
          if (reg.test(file.type) || reg.test(file.name)) {
            newFiles.push(file);
            accepted = true;
            break;
          }
        }
        !accepted && rejectedFiles.push(file);
      }
      if (rejectedFiles.length > 0) {
        /**
         * 未通过 accept 检查事件
         * @param { Array } rejectedFiles
         */
        this.$emit('unaccepted', rejectedFiles);
      }
      return newFiles;
    },
    uploadFiles(files) {
      const newFileObjs = [];

      for (let i = 0; i < files.length; i++) {
        const { file } = files[i];
        const r = files[i].result;

        if (typeof r === 'object' && typeof r.then === 'function') {
          const { then } = r;
          if (typeof then === 'function') {
            then.call(r, (resolveFile) => {
              setTimeout(() => {
                if (!resolveFile) return;

                const isFile = resolveFile instanceof File;
                this.updateFilelist([
                  {
                    key: this.getNewFileKey(),
                    name: file.name,
                    status: FILE_STATUS.READY,
                    file: isFile ? resolveFile : file,
                  },
                ]);
              });
            }).catch(() => {});
          }
        } else if (r || typeof r === 'undefined') {
          const fileObj = {
            key: this.getNewFileKey(),
            name: file.name,
            status: FILE_STATUS.READY,
            file,
          };
          newFileObjs.push(fileObj);
        }
      }

      if (newFileObjs.length > 0) {
        this.updateFilelist(newFileObjs);
      }
    },
    uploadFile(fileObj) {
      const { inputName, url, headers, withCredentials, formData, method } = this;
      const { key } = fileObj;
      const requestProps = {
        key,
        method,
        file: fileObj.file,
        name: inputName,
        url,
        headers,
        withCredentials,
        formData,
        onStart: this.onUploadStart,
        onError: this.onUploadError,
        onSuccess: this.onUploadSuccess,
        onProgress: this.onUploadProgress,
      };

      const xhr = this.request
        ? this.request(requestProps)
        : defaultRequest(requestProps);
      this.uploadList.push({ key, xhr });
    },
    getNewFileKey: (function() {
      let count = 0;
      return function() {
        const key = `${name}-new-${count}`;
        count += 1;
        return key;
      };
    }()),
    getFileObjByKey(key) {
      return this.fileList.find(item => item.key === key);
    },
    fireChange(fileList) {
      this.onChange && this.onChange(fileList);
      /**
       * 文件列表更新事件
       * @param {Array} fileList
       */
      this.$emit('change', fileList);
    },
    updateFilelist(fileObjsToUpdate) {
      const newFileList = [...this.fileList];
      fileObjsToUpdate.forEach((fileObj) => {
        const existFileObj = this.fileList.find(item => item.key === fileObj.key);
        if (existFileObj) {
          const index = this.fileList.indexOf(existFileObj);
          newFileList.splice(index, 1, fileObj);
        } else {
          newFileList.push(fileObj);
        }
      });
      this.fireChange(newFileList);
      return newFileList;
    },
    updateUploadList() {
      for (let i = this.uploadList.length - 1; i >= 0; i--) {
        const { key, xhr } = this.uploadList[i];
        if (!this.fileList.find(fileObj => fileObj.key === key)) {
          xhr && xhr.abort && xhr.abort();
          this.uploadList.splice(i, 1);
        }
      }
    },
    onInputFileChange(e) {
      const { target } = e;
      this.addFiles(target.files);
      target.value = '';
    },
    onSelectFile() {
      this.$refs.inputFile.click();
    },
    onDragOver(e) {
      e.preventDefault();
      this.dragOver = true;
    },
    onDragLeave(e) {
      e.preventDefault();
      this.dragOver = false;
    },
    onDrop(e) {
      e.preventDefault();
      this.dragOver = false;
      this.addFiles(e.dataTransfer.files);
    },
    onRemoveFile(fileObj, idx) {
      if (this.disabled) return;
      if (this.onRemove && !this.onRemove(fileObj)) return;

      const newFileList = [...this.fileList];

      newFileList.splice(idx, 1);
      this.fireChange(newFileList);
    },
    onPreviewFile(fileObj) {
      /**
       * 用户点击文件事件，可用于处理预览或者下载
       * @param {Object} fileObj
       */
      this.$emit('preview', fileObj);
    },
    onUploadStart(key, event) {
      const fileObj = this.getFileObjByKey(key);
      if (!fileObj) return;

      const newFileObj = {
        ...fileObj,
        status: FILE_STATUS.LOADING,
        progress: 0,
        event,
      };
      const newFileList = this.updateFilelist([newFileObj]);
      /**
       * 开始上传事件
       * @param {Object} fileObj
       * @param {Array} fileList
       */
      this.$emit('start', newFileObj, newFileList);
      this.onStart && this.onStart(newFileObj, newFileList);
    },
    onUploadError(key, event) {
      const fileObj = this.getFileObjByKey(key);
      if (!fileObj) return;

      const newFileObj = {
        ...fileObj,
        status: FILE_STATUS.ERROR,
        progress: 0,
        event,
      };
      const newFileList = this.updateFilelist([newFileObj]);
      /**
       * 上传出错事件
       * @param {Object} fileObj
       * @param {Array} fileList
       */
      this.$emit('error', newFileObj, newFileList);
      this.onError && this.onError(newFileObj, newFileList);
    },
    onUploadSuccess(key, event) {
      const fileObj = this.getFileObjByKey(key);
      if (!fileObj) return;

      const responseText = event.target.response || event.target.responseText;
      let response = responseText;

      if (responseText) {
        try {
          response = JSON.parse(responseText);
        } catch (e) {}
      }

      const newFileObj = {
        ...fileObj,
        status: FILE_STATUS.DONE,
        progress: 1,
        response,
        event,
      };
      const newFileList = this.updateFilelist([newFileObj]);
      /**
       * 上传成功事件
       * @param {Object} fileObj
       * @param {Array} fileList
       */
      this.$emit('success', newFileObj, newFileList);
      this.onSuccess && this.onSuccess(newFileObj, newFileList);
    },
    onUploadProgress(key, event) {
      const progress = (event.loaded / event.total) || 0;
      const fileObj = this.getFileObjByKey(key);
      if (!fileObj) return;

      const newFileObj = {
        ...fileObj,
        status: FILE_STATUS.LOADING,
        progress,
        event,
      };
      const newFileList = this.updateFilelist([newFileObj]);
      /**
       * 上传进度更新事件
       * @param {Object} fileObj
       * @param {Array} fileList
       */
      this.$emit('progress', newFileObj, newFileList);
      this.onProgress && this.onProgress(newFileObj, newFileList);
    },
  },
};
</script>
